import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ArrowLeft, LayoutDashboard, UserCircle2, BookOpen, ClipboardList,
  MessagesSquare, Sparkles, Gauge, Award, CheckCircle2, Circle, Send, Loader2,
  Trash2, Plus, Printer, GraduationCap, Building2, Lock,
} from "lucide-react";
import LiveInterview from "@/components/LiveInterview";

const ACCENT = "#0ea5a3";

const NAV = [
  { id: "journey", label: "Journey", icon: LayoutDashboard },
  { id: "profile", label: "Your Profile", icon: UserCircle2 },
  { id: "learn", label: "Learn", icon: BookOpen },
  { id: "project", label: "Risk Assessment", icon: ClipboardList },
  { id: "interview", label: "Mock Interview", icon: MessagesSquare },
  { id: "coach", label: "GRC Coach", icon: Sparkles },
  { id: "readiness", label: "Readiness", icon: Gauge },
  { id: "certificate", label: "Certificate", icon: Award },
];

const EXPERIENCE = ["New to cybersecurity", "Cybersecurity, new to GRC", "0–1 year GRC", "1–3 years GRC",
  "3–5 years GRC", "Senior GRC", "IT Audit background", "Security Operations background",
  "IT Support / SysAdmin background", "Cloud background", "IAM background"];
const ROLES = ["GRC Analyst", "GRC Specialist", "Cybersecurity Compliance Analyst", "Risk Analyst",
  "IT Risk Analyst", "IT Auditor", "Security Compliance Analyst", "Third-Party Risk Analyst",
  "Security Control Assessor", "RMF Analyst", "Cybersecurity Governance Analyst", "Senior GRC Analyst"];
const INDUSTRIES = ["Healthcare", "Banking / Financial Services", "Federal Government", "Defense Contractor",
  "Technology / SaaS", "Retail", "Energy", "Manufacturing", "Education", "General Enterprise"];
const FRAMEWORKS = ["NIST CSF", "NIST RMF", "NIST 800-53", "ISO 27001", "SOC 1", "SOC 2", "PCI DSS",
  "HIPAA", "SOX", "COBIT", "COSO", "GDPR", "FedRAMP", "CMMC", "CIS Controls"];

function renderMd(text) {
  // lightweight: headings, bold, bullets, blockquote
  return (text || "").split("\n").map((raw, i) => {
    const line = raw.replace(/\*\*(.+?)\*\*/g, "‹b›$1‹/b›");
    const parts = line.split(/(‹b›.+?‹\/b›)/g).map((p, j) =>
      p.startsWith("‹b›") ? <strong key={j}>{p.slice(3, -4)}</strong> : <span key={j}>{p}</span>);
    if (raw.startsWith("### ")) return <h4 key={i} className="font-semibold mt-3">{raw.slice(4)}</h4>;
    if (raw.startsWith("## ")) return <h3 key={i} className="font-semibold text-base mt-3">{raw.slice(3)}</h3>;
    if (raw.startsWith("# ")) return <h3 key={i} className="font-bold text-base mt-2">{raw.slice(2)}</h3>;
    if (raw.startsWith("> ")) return <blockquote key={i} className="border-l-2 border-[color:var(--acc)] pl-3 my-1 text-muted-foreground">{parts}</blockquote>;
    if (/^[-*] /.test(raw)) return <li key={i} className="ml-4 list-disc">{parts}</li>;
    if (raw.trim() === "---" || raw.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i} className="my-0.5">{parts}</p>;
  });
}

export default function GrcLab() {
  const navigate = useNavigate();
  const [view, setView] = useState("journey");
  const [ov, setOv] = useState(null);
  const loadOv = () => api.get("/grc/overview").then(({ data }) => setOv(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { loadOv(); }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background text-foreground" style={{ ["--acc"]: ACCENT }} data-testid="grc-lab">
      <header className="h-14 shrink-0 flex items-center gap-3 px-4 border-b border-border" style={{ background: "linear-gradient(90deg,#0b2b2b,#0ea5a3)" }}>
        <button onClick={() => navigate("/interview-prep")} className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm" data-testid="grc-back"><ArrowLeft className="h-4 w-4" /> Interview Prep</button>
        <span className="text-white/40">/</span>
        <div className="flex items-center gap-2 text-white font-semibold"><ShieldCheck className="h-5 w-5" /> CS GRC Analyst</div>
        <button onClick={() => navigate("/labs")} className="ml-auto text-white/80 hover:text-white text-sm" data-testid="grc-back-labs">HMG Labs</button>
        {ov && <span className="text-white text-xs bg-white/15 rounded-full px-2.5 py-1" data-testid="grc-progress">{ov.progress.done}/{ov.progress.total} steps</span>}
      </header>

      <div className="flex-1 flex overflow-hidden">
        <nav className="w-56 shrink-0 border-r border-border bg-card overflow-y-auto py-3">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setView(n.id)} data-testid={`grc-nav-${n.id}`}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm ${view === n.id ? "bg-[color:var(--acc)]/10 text-[color:var(--acc)] font-medium border-r-2 border-[color:var(--acc)]" : "text-muted-foreground hover:bg-accent"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <main className="flex-1 overflow-y-auto">
          {!ov ? <div className="h-full grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div> : (
            <>
              {view === "journey" && <Journey ov={ov} go={setView} />}
              {view === "profile" && <Profile ov={ov} reload={loadOv} />}
              {view === "learn" && <Learn reload={loadOv} />}
              {view === "project" && <Project reload={loadOv} />}
              {view === "interview" && <Interview ov={ov} reload={loadOv} />}
              {view === "coach" && <Coach />}
              {view === "readiness" && <Readiness />}
              {view === "certificate" && <Certificate />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function Section({ title, desc, children, testid }) {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid={testid}>
      <h2 className="text-xl font-bold">{title}</h2>
      {desc && <p className="text-sm text-muted-foreground mt-1">{desc}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

function Journey({ ov, go }) {
  const pct = Math.round(100 * ov.progress.done / ov.progress.total);
  return (
    <Section title={`Welcome to the ${ov.org.your_role} track`} desc="Learn → Perform → Interview → Graduate. Complete every step to earn your certificate." testid="grc-journey">
      <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-4 mb-6">
        <Building2 className="h-8 w-8 text-[color:var(--acc)]" />
        <div>
          <div className="font-semibold">{ov.org.name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{ov.org.profile}</div>
        </div>
      </div>
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-1"><span className="font-medium">Overall progress</span><span className="text-muted-foreground">{pct}%</span></div>
        <div className="h-2.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ACCENT }} /></div>
      </div>
      <div className="space-y-2">
        {ov.steps.map((s) => (
          <button key={s.id} onClick={() => go(stepToView(s.id))} data-testid={`grc-step-${s.id}`}
            className="w-full flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:border-[color:var(--acc)] transition-colors">
            {s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
            <span className={`text-sm ${s.done ? "text-muted-foreground line-through" : "font-medium"}`}>{s.label}</span>
          </button>
        ))}
      </div>
      {ov.graduated && (
        <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-center gap-3">
          <GraduationCap className="h-8 w-8 text-emerald-500" />
          <div className="flex-1"><div className="font-semibold text-emerald-700 dark:text-emerald-300">You've completed every step!</div>
            <div className="text-sm text-muted-foreground">Head to the Certificate tab to claim your Certificate of Completion.</div></div>
          <button onClick={() => go("certificate")} className="text-sm font-medium text-emerald-600 hover:underline" data-testid="grc-goto-cert">View certificate →</button>
        </div>
      )}
    </Section>
  );
}
function stepToView(id) {
  if (id === "onboarding") return "profile";
  if (id === "learn") return "learn";
  if (id === "project") return "project";
  return "interview";
}

function Profile({ ov, reload }) {
  const p = ov.profile || {};
  const [f, setF] = useState({ experience: p.experience || "", target_role: p.target_role || "", industry: p.industry || "", frameworks: p.frameworks || [] });
  const [busy, setBusy] = useState(false);
  const toggleFw = (fw) => setF((s) => ({ ...s, frameworks: s.frameworks.includes(fw) ? s.frameworks.filter((x) => x !== fw) : [...s.frameworks, fw] }));
  const save = async () => {
    if (!f.experience || !f.target_role || !f.industry) return toast.error("Please choose your experience, target role and industry.");
    setBusy(true);
    try { await api.post("/grc/profile", f); toast.success("Profile saved. Your interviews will be tailored to this."); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const Group = ({ label, options, value, onPick, testid }) => (
    <div className="mb-6">
      <div className="text-sm font-medium mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} data-testid={`${testid}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} onClick={() => onPick(o)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value === o ? "text-white border-transparent" : "border-border hover:border-[color:var(--acc)]"}`}
            style={value === o ? { background: ACCENT } : {}}>{o}</button>
        ))}
      </div>
    </div>
  );
  return (
    <Section title="Your analyst profile" desc="Tell us where you are and where you're headed. Interviews, coaching and your 'Tell me about yourself' answer adapt to this." testid="grc-profile">
      <Group label="Experience" options={EXPERIENCE} value={f.experience} onPick={(o) => setF({ ...f, experience: o })} testid="exp" />
      <Group label="Target role" options={ROLES} value={f.target_role} onPick={(o) => setF({ ...f, target_role: o })} testid="role" />
      <Group label="Target industry" options={INDUSTRIES} value={f.industry} onPick={(o) => setF({ ...f, industry: o })} testid="ind" />
      <div className="mb-6">
        <div className="text-sm font-medium mb-2">Framework experience (select any)</div>
        <div className="flex flex-wrap gap-2">
          {FRAMEWORKS.map((fw) => (
            <button key={fw} data-testid={`fw-${fw.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} onClick={() => toggleFw(fw)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.frameworks.includes(fw) ? "text-white border-transparent" : "border-border hover:border-[color:var(--acc)]"}`}
              style={f.frameworks.includes(fw) ? { background: ACCENT } : {}}>{fw}</button>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={busy} data-testid="grc-profile-save" className="px-5 py-2.5 rounded-lg text-white text-sm font-medium flex items-center gap-2" style={{ background: ACCENT }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save profile
      </button>
    </Section>
  );
}

function Learn({ reload }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/grc/lessons").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const complete = async (id) => {
    try { await api.post(`/grc/lessons/${id}/complete`); toast.success("Lesson complete"); await load(); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const done = data.done || [];
  return (
    <Section title="Fundamentals & Frameworks" desc="Short, interview-focused lessons. Finish all of them to clear the Learn step." testid="grc-learn">
      <div className="space-y-3">
        {data.lessons.map((l) => {
          const isDone = done.includes(l.id);
          const isOpen = open === l.id;
          return (
            <div key={l.id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`lesson-${l.id}`}>
              <button onClick={() => setOpen(isOpen ? null : l.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                {isDone ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
                <div className="flex-1">
                  <div className="font-medium text-sm">{l.title}</div>
                  <div className="text-xs text-muted-foreground">{l.category} · {l.minutes} min · {l.summary}</div>
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 border-t border-border">
                  <ul className="space-y-2 my-3">
                    {l.key_points.map((k, i) => (
                      <li key={i} className="flex gap-2 text-sm"><span className="text-[color:var(--acc)] mt-0.5">▸</span><span>{k}</span></li>
                    ))}
                  </ul>
                  <button onClick={() => complete(l.id)} data-testid={`lesson-complete-${l.id}`} disabled={isDone}
                    className="px-4 py-2 rounded-lg text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-50" style={{ background: ACCENT }}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> {isDone ? "Completed" : "Mark as complete"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Project({ reload }) {
  const [data, setData] = useState(null);
  const blank = { risk_description: "", asset: "", threat: "", vulnerability: "", existing_controls: "", likelihood: "Medium", impact: "High", treatment: "Mitigate", additional_controls: "", risk_owner: "", target_date: "", residual_likelihood: "Low", residual_impact: "Medium", status: "Open" };
  const [row, setRow] = useState(blank);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/grc/project").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!row.risk_description || !row.asset || !row.risk_owner) return toast.error("Risk description, asset and risk owner are required.");
    setBusy(true);
    try { await api.post("/grc/register", { row }); setRow(blank); await load(); toast.success("Risk added to register"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/grc/register/${id}`); await load(); };
  const submit = async () => {
    try { await api.post("/grc/project/submit"); toast.success("Risk assessment submitted. Project complete!"); await load(); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const P = data.project;
  const scale = P.rating_scale;
  const inp = "w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm";
  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="grc-project">
      <h2 className="text-xl font-bold">{P.title}</h2>
      <p className="text-sm text-muted-foreground mt-1">{P.org.name}</p>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="font-semibold text-sm mb-1">Scenario</div>
            <p className="text-sm text-muted-foreground">{P.scenario}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="font-semibold text-sm mb-2">In-scope assets</div>
            <ul className="text-sm space-y-1">{P.assets.map((a, i) => <li key={i} className="flex justify-between gap-2"><span>{a.name}</span><span className="text-xs text-muted-foreground">{a.data}</span></li>)}</ul>
          </div>
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <div className="font-semibold text-sm mb-2">Assessment findings</div>
            <ul className="text-sm space-y-1.5">{P.findings.map((fnd, i) => <li key={i} className="flex gap-2"><span className="text-amber-500">⚠</span><span>{fnd}</span></li>)}</ul>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="font-semibold text-sm mb-2">Your task</div>
            <ol className="text-sm space-y-1 list-decimal ml-4 text-muted-foreground">{P.instructions.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="font-semibold text-sm mb-3">Add a risk to the register</div>
            <div className="grid grid-cols-2 gap-2">
              <input className={`${inp} col-span-2`} placeholder="Risk description" value={row.risk_description} onChange={(e) => setRow({ ...row, risk_description: e.target.value })} data-testid="risk-desc" />
              <input className={inp} placeholder="Asset" value={row.asset} onChange={(e) => setRow({ ...row, asset: e.target.value })} data-testid="risk-asset" />
              <input className={inp} placeholder="Threat" value={row.threat} onChange={(e) => setRow({ ...row, threat: e.target.value })} />
              <input className={inp} placeholder="Vulnerability" value={row.vulnerability} onChange={(e) => setRow({ ...row, vulnerability: e.target.value })} />
              <input className={inp} placeholder="Existing controls" value={row.existing_controls} onChange={(e) => setRow({ ...row, existing_controls: e.target.value })} />
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Likelihood
                <select className={inp} value={row.likelihood} onChange={(e) => setRow({ ...row, likelihood: e.target.value })}>{scale.map((s) => <option key={s}>{s}</option>)}</select></label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Impact
                <select className={inp} value={row.impact} onChange={(e) => setRow({ ...row, impact: e.target.value })}>{scale.map((s) => <option key={s}>{s}</option>)}</select></label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Treatment
                <select className={inp} value={row.treatment} onChange={(e) => setRow({ ...row, treatment: e.target.value })}>{P.treatments.map((s) => <option key={s}>{s}</option>)}</select></label>
              <input className={inp} placeholder="Risk owner" value={row.risk_owner} onChange={(e) => setRow({ ...row, risk_owner: e.target.value })} data-testid="risk-owner" />
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Residual likelihood
                <select className={inp} value={row.residual_likelihood} onChange={(e) => setRow({ ...row, residual_likelihood: e.target.value })}>{scale.map((s) => <option key={s}>{s}</option>)}</select></label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Residual impact
                <select className={inp} value={row.residual_impact} onChange={(e) => setRow({ ...row, residual_impact: e.target.value })}>{scale.map((s) => <option key={s}>{s}</option>)}</select></label>
            </div>
            <button onClick={add} disabled={busy} data-testid="risk-add" className="mt-3 px-4 py-2 rounded-lg text-white text-xs font-medium flex items-center gap-1.5" style={{ background: ACCENT }}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add to register
            </button>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-sm">Risk register ({data.register.length})</div>
              {data.submitted ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Submitted</span>
                : <button onClick={submit} disabled={data.register.length < 3} data-testid="risk-submit" className="text-xs px-3 py-1.5 rounded-md text-white font-medium disabled:opacity-40" style={{ background: ACCENT }}>Submit assessment</button>}
            </div>
            {data.register.length === 0 ? <p className="text-sm text-muted-foreground">No risks yet. Add at least 3 to submit.</p> : (
              <div className="space-y-2" data-testid="risk-register-list">
                {data.register.map((r) => (
                  <div key={r.risk_id} className="rounded-lg border border-border p-2.5 text-xs" data-testid={`risk-row-${r.risk_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.risk_id} · {r.risk_description}</span>
                      <button onClick={() => del(r.risk_id)} className="text-red-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="text-muted-foreground mt-1">{r.asset} · L:{r.likelihood}/I:{r.impact} → {r.treatment} · owner {r.risk_owner} · residual L:{r.residual_likelihood}/I:{r.residual_impact}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Interview({ ov, reload }) {
  const modes = [
    { id: "recruiter", label: "Recruiter Screening", desc: "Background, motivation & communication.", best: ov.interviews.recruiter.best_score },
    { id: "hiring_manager", label: "Hiring Manager", desc: "Deep dive into real hands-on experience with follow-ups.", best: ov.interviews.hiring_manager.best_score },
    { id: "technical", label: "Technical GRC", desc: "Precise definitions, control testing, frameworks.", best: ov.interviews.technical.best_score },
  ];
  return <LiveInterview apiBase="grc" accent={ACCENT} testidPrefix="grc" onGraded={reload} modes={modes} />;
}
function Coach() {
  const [msgs, setMsgs] = useState([{ role: "coach", text: "Hi, I'm your GRC Coach. Ask me anything, like 'What is residual risk?', 'How do I test an MFA control?', or 'Give me a practice question.' You can also generate a tailored 'Tell me about yourself' answer below." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const ask = async (text) => {
    const t = (text || input).trim(); if (!t || busy) return;
    setMsgs((m) => [...m, { role: "you", text: t }]); setInput(""); setBusy(true);
    try { const { data } = await api.post("/grc/coach", { text: t }); setMsgs((m) => [...m, { role: "coach", text: data.answer }]); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const tmays = async () => {
    setBusy(true);
    try { const { data } = await api.post("/grc/tell-me-about-yourself"); setMsgs((m) => [...m, { role: "you", text: "Help me answer: Tell me about yourself." }, { role: "coach", text: data.answer }]); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl mx-auto px-6 py-8 h-full flex flex-col" data-testid="grc-coach">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Ask GRC Coach</h2>
        <button onClick={tmays} disabled={busy} data-testid="coach-tmays" className="text-xs px-3 py-2 rounded-lg text-white font-medium flex items-center gap-1.5" style={{ background: ACCENT }}><Sparkles className="h-3.5 w-3.5" /> Craft my "Tell me about yourself"</button>
      </div>
      <div className="flex-1 overflow-y-auto rounded-xl border border-border bg-card p-4 space-y-3">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "you" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`coach-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "you" ? "text-white" : "bg-muted"} [&_li]:my-0.5`} style={{ ...(m.role === "you" ? { background: ACCENT } : {}), ["--acc"]: ACCENT }}>
              {m.role === "you" ? m.text : <div>{renderMd(m.text)}</div>}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-muted rounded-2xl px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex items-end gap-2 mt-3">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} rows={2}
          placeholder="Ask the coach…" data-testid="coach-input" className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none" />
        <button onClick={() => ask()} disabled={busy} data-testid="coach-send" className="h-10 w-10 shrink-0 rounded-lg text-white grid place-items-center" style={{ background: ACCENT }}><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function Readiness() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/grc/readiness").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const bar = (v) => v >= 80 ? "#10b981" : v >= 60 ? "#0ea5a3" : v >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <Section title="Interview Readiness" desc="Your readiness across GRC competencies, computed from lessons, your project and interview scores." testid="grc-readiness">
      <div className="rounded-xl border border-border bg-card p-6 mb-6 text-center">
        <div className="text-5xl font-bold" style={{ color: bar(data.overall) }}>{data.overall}%</div>
        <div className="text-sm text-muted-foreground mt-1">Overall readiness</div>
      </div>
      <div className="space-y-3">
        {Object.entries(data.categories).map(([k, v]) => (
          <div key={k} data-testid={`readiness-${k.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
            <div className="flex justify-between text-sm mb-1"><span>{k}</span><span className="text-muted-foreground">{v}%</span></div>
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: bar(v) }} /></div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Certificate() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/grc/certificate").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!data.eligible) {
    return (
      <Section title="Certificate of Completion" testid="grc-certificate">
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <Lock className="h-10 w-10 text-muted-foreground mx-auto" />
          <div className="font-semibold mt-3">Not unlocked yet</div>
          <p className="text-sm text-muted-foreground mt-1">Complete every journey step to unlock your certificate:</p>
          <ul className="text-sm text-left max-w-sm mx-auto mt-3 space-y-1">
            {data.remaining.map((r, i) => <li key={i} className="flex gap-2"><Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />{r}</li>)}
          </ul>
        </div>
      </Section>
    );
  }
  const c = data.certificate;
  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="grc-certificate">
      <div className="flex justify-end mb-4 print:hidden">
        <button onClick={() => window.print()} data-testid="cert-print" className="text-sm px-4 py-2 rounded-lg text-white font-medium flex items-center gap-1.5" style={{ background: ACCENT }}><Printer className="h-4 w-4" /> Print / Save PDF</button>
      </div>
      <div id="grc-cert" className="rounded-2xl border-4 p-10 text-center bg-card" style={{ borderColor: ACCENT }}>
        <ShieldCheck className="h-14 w-14 mx-auto" style={{ color: ACCENT }} />
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-4">HMG Global Consults · Interview Prep Lab</div>
        <h1 className="text-2xl font-bold mt-3">{data.title}</h1>
        <p className="text-sm text-muted-foreground mt-6">This certifies that</p>
        <div className="text-3xl font-bold my-2" style={{ color: ACCENT }}>{c.name}</div>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto">has completed the hands-on CS GRC Analyst interview-preparation lab, performing a full risk assessment, building a risk register, and clearing the recruiter, hiring-manager and technical GRC interviews.</p>
        <div className="text-lg font-semibold mt-6" style={{ color: ACCENT }}>🎉 {data.message}</div>
        <div className="flex flex-wrap justify-center gap-1.5 mt-6">
          {data.skills.map((s) => <span key={s} className="text-[11px] px-2.5 py-1 rounded-full bg-muted">{s}</span>)}
        </div>
        <div className="flex justify-between items-end mt-8 text-xs text-muted-foreground border-t border-border pt-4">
          <div className="text-left">Certificate ID<br /><span className="font-mono text-foreground">{c.id}</span></div>
          <div className="text-right">Issued<br /><span className="text-foreground">{c.issued}</span></div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-4 italic">{data.disclaimer}</p>
      </div>
    </div>
  );
}
